import { NGXLogger } from 'ngx-logger';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';

/** Shown when this session's own 401 is traced to a backend session-kick marker (see
 *  JwtRequestFilter's SESSION_INVALIDATED_ELSEWHERE branch) — i.e. this device was signed out
 *  because the same account logged in from somewhere else and confirmed "log out that device".
 *  device/location describe the NEW (kicking) login, not this one. */
export interface ForcedLogoutDialogRequest {
  device: string | null;
  location: string | null;
  kickedAtEpochMillis: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class ForcedLogoutDialogService {
  private readonly logger = inject(NGXLogger);
  private readonly router = inject(Router);

  private readonly dialogRequestSource = new Subject<ForcedLogoutDialogRequest>();
  readonly dialogRequest$ = this.dialogRequestSource.asObservable();

  // Sticky while the dead session is being handled (reset() is called once "Go to Login" has been pressed and the
  // login page is showing). A dead session 401s on several
  // concurrent requests at once, but the backend kick marker is consume-once
  // (ValkeyTokenService.getAndConsumeForcedLogoutMarker) — so exactly ONE of those 401s carries
  // SESSION_INVALIDATED_ELSEWHERE and opens this dialog, while the siblings fall through to the
  // generic refresh-then-session-expired path and stacked a second "Session Expired" dialog on
  // top of this one (both are p-dialog at baseZIndex 25000, so the later one wins and hid the
  // device/location card entirely — real bug seen on PROD 2026-08-04).
  //
  // Once we know this device was kicked, "your session expired" is strictly less accurate than
  // what this dialog already says, and there is no path out of the forced-logout dialog except
  // Go to Login ([closable]="false", single button) — so suppressing the generic one from here
  // on can never strand the user.
  private kicked = false;

  isActive(): boolean {
    return this.kicked;
  }

  /** Forget that this device was kicked — called once the person has pressed "Go to Login" and the login page is
   *  showing, so a later, genuine session expiry in the same page life can show its own dialog again. */
  reset(): void {
    this.kicked = false;
  }

  open(request: ForcedLogoutDialogRequest): void {
    const onSuppressedRoute = this.isOnLoginOrPublicRoute();
    if (onSuppressedRoute === true) {
      this.logger.debug('[ForcedLogoutDialogService] Not opening the dialog: already on the login / public page');
      return;
    }
    if (onSuppressedRoute === null) {
      // Router has not committed a route yet (cold start, '' -> /login redirect in progress): decide one tick later,
      // the same trick the session-expired dialog uses.
      setTimeout(() => {
        if (this.isOnLoginOrPublicRoute() === true) {
          this.logger.debug('[ForcedLogoutDialogService] Not opening the dialog: landed on the login / public page');
          return;
        }
        this.show(request);
      });
      return;
    }
    this.show(request);
  }

  private show(request: ForcedLogoutDialogRequest): void {
    this.logger.debug('[ForcedLogoutDialogService] Opening forced-logout-elsewhere dialog', request);
    this.kicked = true;
    this.dialogRequestSource.next(request);
  }

  /** The dialog says "you were signed out - go to login": pointless (and wrong) on the login page itself, or on pages
   *  that are opened without an account (public share views). true = suppress, false = show, null = not known yet. */
  private isOnLoginOrPublicRoute(): boolean | null {
    const current = this.router.url;
    const pending = this.router.getCurrentNavigation()?.extractedUrl?.toString() ?? '';
    if (current === '/' && !pending) {
      return null;
    }
    const suppressed = (r: string) =>
      r.startsWith('/login')
      || r.startsWith('/s/')
      || r === '/s'
      || r.startsWith('/tally-sheet/share')
      || r.startsWith('/tallysheet-view')
      || r.startsWith('/public/view/');
    return suppressed(current) || suppressed(pending);
  }
}
